<template>
  <div class="custom-tree-select-container" ref="containerRef">     
    <!-- 选择框头部 -->
    <div class="selector-header" @click="toggleDropdown" :class="{ 'open': dropdownVisible }">
      <div v-if="selectedDevice" class="selected-tags">
        <el-tag
          closable
          @close="clearSelectedDevice"
          class="device-tag"
        >
          {{ selectedDevice.name }}
        </el-tag>
      </div>
      <span v-else class="placeholder">{{ t('message.realTimeData.selectDevice') }}</span>
      
      <div 
        class="dropdown-icon" 
        @mouseenter="isHovering = true"
        @mouseleave="isHovering = false"
        @click.stop="handleIconClick"
      >
        <el-icon :class="{ 'reverse': dropdownVisible }">
          <component :is="getIconComponent" />
        </el-icon>
      </div>      
    </div>
    
    <!-- 下拉面板内容 -->
    <div 
      v-show="dropdownVisible" 
      class="selector-dropdown" 
      @click.stop
    >
      <div class="selector-panel">
        <!-- 企业树面板 -->
        <div class="tree-panel">          
          <div class="search-container">
            <el-input
              v-model="treeSearch"
              :placeholder="$t('message.CompDeviceTree.searchDevice')"
              size="small"
              clearable
              prefix-icon="Search"
              @input="handleTreeSearch"
            />
          </div>
          <el-scrollbar class="tree-list">
            <el-tree
              ref="treeRef"
              :data="filteredTreeData"
              :props="treeProps"
              node-key="Key"
              :expand-on-click-node="false"
              :highlight-current="true"
              :filter-node-method="filterTreeNode"
              @node-click="handleTreeNodeClick"
              v-loading="isTreeLoading"
              :empty-text="emptyText"
              :default-expand-all="!treeSearch"
            >
              <template #default="{ node, data }">
                <div class="tree-node-content">
                  <el-icon v-if="data.type === 2" class="device-icon">
                    <Monitor />
                  </el-icon>
                  <el-icon v-else-if="data.type === 1" class="company-icon">
                    <OfficeBuilding />
                  </el-icon>
                  <el-icon v-else class="default-icon">
                    <Folder />
                  </el-icon>
                  <span class="node-label">{{ node.label }}</span>
                  <span v-if="data.type === 2 && data.deviceCode" class="device-code">
                    ({{ data.deviceCode }})
                  </span>
                </div>
              </template>
            </el-tree>
          </el-scrollbar>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick,defineProps,defineEmits } from 'vue';
import { useEventListener } from '@vueuse/core';
import { getCompanyDeviceTree } from '@/api/CompanyManager'
import { ArrowDown, Close, Monitor, OfficeBuilding, Folder } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus';
import { useI18n } from 'vue-i18n'

// 定义 Props
const props = defineProps({
  modelValue: {
    type: [Object, Number, String],
    default: null
  },
  srcCompanyId: {
    type: [Object, Number, String],
    default: null
  },
  // 新增：外部传入的设备对象
  externalDevice: {
    type: Object,
    default: null
  },  
  // 新增：外部传入的设备ID
  externalDeviceId: {
    type: [Number, String],
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'change', 'node-click'])

const { t } = useI18n()
const containerRef = ref(null);
const dropdownVisible = ref(false);
const treeSearch = ref('');

// 企业树相关
const treeRef = ref(null);
const deviceTreeOptions = ref([]);
const isTreeLoading = ref(false);
const selectedDevice = ref(null);
const emptyText = ref(t('message.realTimeData.noData'));
const expandedState = ref(null);
const isHovering = ref(false);
const currentSelectedNodeData = ref(null);
const currentSelectedNode = ref(null);

// 树配置
const treeProps = {
  children: 'children',
  label: 'name'
}

// 加载设备树
const loadDeviceTree = async () => {
  try {
    isTreeLoading.value = true;
    const res = await getCompanyDeviceTree({ companyId: props.srcCompanyId });
    
    if (res.success) {
      deviceTreeOptions.value = formatDeviceTree(res.data.list || []);
      
      // 加载完成后，如果有外部传入的设备，自动选中
      await nextTick();
      
      if (props.externalDevice || props.externalDeviceId) {        
        selectExternalDevice();
      }
    } else {
      ElMessage.error(res.msg || t('message.realTimeData.loadDeviceTreeFailed'));
    }
  } catch (error) {
    ElMessage.error(t('message.realTimeData.loadDeviceTreeFailed'));
  } finally {
    isTreeLoading.value = false;
  }
};

// 选择外部传入的设备
const selectExternalDevice = () => {
    try
    {
        let deviceToSelect = null;
        
        // 优先使用 externalDevice
        if (props.externalDevice && props.externalDevice.id) {
          deviceToSelect = props.externalDevice;
        } 
        // 其次使用 externalDeviceId
        else if (props.externalDeviceId) {
          const device = findNodeById(deviceTreeOptions.value, props.externalDeviceId);
          if (device && device.type === 2) {
            deviceToSelect = device;
          }
        }
        
        if (deviceToSelect) {
          selectedDevice.value = {
            key:`${deviceToSelect.type}_${deviceToSelect.id}`,
            id: deviceToSelect.id,
            name: deviceToSelect.name || deviceToSelect.label,
            deviceCode: deviceToSelect.deviceCode,
            type: deviceToSelect.type
          };
          //ElMessage.error(`${deviceToSelect.key} selectExternalDevice`);
          // 在树中选中对应的节点
          if (treeRef.value) {
            treeRef.value.setCurrentKey(deviceToSelect.key);
          }    
          console.log('外部设备已选中:', selectedDevice.value);
        }
    } catch (error) 
    {
        ElMessage.error(error);
    } finally {
      isTreeLoading.value = false;
    }
};

// 格式化设备树数据
const formatDeviceTree = (treeData) => {
  return treeData.map(item => ({
    id: item.id,
    name: item.name,
    type: item.type,
    parentId: item.parentId,
    deviceCode: item.data?.device_code || '',
    key:item.key,
    children: item.children ? formatDeviceTree(item.children) : []
  }));
};

// 树节点点击事件
const handleTreeNodeClick = (nodeData, node) => {
  // 只有设备节点(type=2)才可以选择
  if (nodeData.type === 2) {
    selectedDevice.value = {
      id: nodeData.id,
      name: nodeData.name,
      deviceCode: nodeData.deviceCode,
      type: nodeData.type
    };

    // 关闭下拉框
    closeDropdown();
    currentSelectedNodeData.value = nodeData;
    currentSelectedNode.value = node;
    
    // 触发事件的顺序不可变动
    emit('node-click', currentSelectedNodeData.value, currentSelectedNode.value);
    emit('update:modelValue', selectedDevice.value);      
    emit('change', selectedDevice.value);
  } else {
    // 非设备节点：如果有子节点，则展开/收缩
    if (nodeData.children && nodeData.children.length > 0) {
      // 使用正确的方法切换展开状态
      toggleNodeExpansion(node);
    } else {
      // 没有子节点的非设备节点，提示只能选择设备
      ElMessage.warning(t('message.realTimeData.selectDeviceOnly'));
    }
  }
};

// 切换节点展开状态
const toggleNodeExpansion = (node) => {
  if (!treeRef.value) return;  
  try {
    // 判断是否是根节点
    const isRootNode = node.level === 1;      
    if (isRootNode) {
      if (expandedState.value != null) {
        node.expanded = !expandedState.value;
      } else {
        node.expanded = !node.expanded;
      }
      expandedState.value = node.expanded;
    } else {
      // 获取节点的真实展开状态
      const nodeKey = node.data.id;
      const isCurrentlyExpanded = treeRef.value.store.nodesMap[nodeKey]?.expanded;
      
      if (isCurrentlyExpanded) {
        treeRef.value.store.collapse(node.data);
      } else {
        treeRef.value.store.expand(node.data);
      }
    }
  } catch (error) {
    console.error('切换节点展开状态失败:', error);
    try {
      if (node.expanded) {
        node.collapse();
      } else {
        node.expand();
      }
    } catch (fallbackError) {
      console.error('备选方案也失败:', fallbackError);
    }
  }
};

// 树节点过滤
const filterTreeNode = (value, data) => {
  if (!value) return true;
  const keyword = value.toLowerCase();
  return (
    (data.name && data.name.toLowerCase().includes(keyword)) ||
    (data.deviceCode && data.deviceCode.toLowerCase().includes(keyword))
  );
};

// 树搜索处理
const handleTreeSearch = () => {
  if (treeRef.value) {
    treeRef.value.filter(treeSearch.value);
  }
};

// 过滤后的树数据
const filteredTreeData = computed(() => {
  if (!treeSearch.value) {
    return deviceTreeOptions.value;
  }
  
  const keyword = treeSearch.value.toLowerCase();
  
  const filterTree = (nodes) => {
    const result = [];
    
    for (const node of nodes) {
      const isMatch = (
        (node.name && node.name.toLowerCase().includes(keyword)) ||
        (node.deviceCode && node.deviceCode.toLowerCase().includes(keyword))
      );
      
      if (node.type === 2 && isMatch) {
        result.push({ ...node });
        continue;
      }
      
      if (node.children && node.children.length > 0) {
        const filteredChildren = filterTree(node.children);
        if (filteredChildren.length > 0) {
          result.push({
            ...node,
            children: filteredChildren
          });
        }
      }
    }
    
    return result;
  };
  
  return filterTree(JSON.parse(JSON.stringify(deviceTreeOptions.value)));
});

// 根据ID查找节点
const findNodeById = (data, id) => {
  for (const item of data) {
    // if (item.id == id) {  // 使用 == 而不是 ===，因为可能类型不同
    //   return item;
    // }
    if (Number(item.id) === Number(id) && Number(item.type) === 2) 
    {        
        //   ElMessage.error(`id:${id}--nodeid:${node.id}--type:${node.type}-name:${node.name}-1-1- handleDeviceByRotue`);
        // ElMessage.error(`${node.id}-1-1- handleDeviceByRotue*************`);
        return item;
    }
    if (item.children && item.children.length > 0) {
      const found = findNodeById(item.children, id);
      if (found) return found;
    }
  }
  return null;
};

// 计算图标组件
const getIconComponent = computed(() => {
  if (selectedDevice.value && isHovering.value) {
    return Close;
  }
  return ArrowDown;
});

// 图标点击处理
const handleIconClick = () => {
  if (selectedDevice.value && isHovering.value) {
    clearSelectedDevice();
  } else {
    toggleDropdown();
  }
};

// 清空选中的设备
const clearSelectedDevice = () => {
  selectedDevice.value = null;
  if (treeRef.value) {
    treeRef.value.setCurrentKey(null);
  }
  emit('update:modelValue', null);
  emit('change', null);
};

// 打开下拉框
const toggleDropdown = () => {
  dropdownVisible.value = !dropdownVisible.value;
   
  if (dropdownVisible.value) {
    if (deviceTreeOptions.value.length === 0) {        
      loadDeviceTree();           
    } 
  }   
};

// 关闭下拉框
const closeDropdown = () => {
  dropdownVisible.value = false;
};

// 点击外部关闭下拉框
useEventListener('click', (e) => {
  if (containerRef.value && !containerRef.value.contains(e.target)) {
    closeDropdown();
  }
});

onMounted(async () => {
  await loadDeviceTree();
  await new Promise(resolve => setTimeout(resolve, 100));
});

// 监听外部值变化
watch(() => props.modelValue, (newVal) => {
  if (newVal && typeof newVal === 'object') {
    selectedDevice.value = newVal;
  } else if (newVal && deviceTreeOptions.value.length > 0) {
    const device = findNodeById(deviceTreeOptions.value, newVal);
    if (device && device.type === 2) {
      selectedDevice.value = {
        id: device.id,
        name: device.name,
        deviceCode: device.deviceCode,
        type: device.type
      };
    }
  } else {
    selectedDevice.value = null;
  }
}, { immediate: true });

// 监听外部传入的设备变化
watch(() => props.externalDevice, (newDevice) => {
  if (newDevice && deviceTreeOptions.value.length > 0) {
    selectExternalDevice();
  }
}, { deep: true });

// 监听外部传入的设备ID变化
watch(() => props.externalDeviceId, (newId) => {
  if (newId && deviceTreeOptions.value.length > 0) {    
    selectExternalDevice();
  }
});
</script>

<style scoped>
/* 样式保持不变 */
.custom-tree-select-container {
  width: 100%;
  position: relative;
  display: inline-block;
}

.selector-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid var(--el-border-color);
  border-radius: 4px;
  cursor: pointer;
  background-color: #fff;
  user-select: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  min-height: 40px;
  box-sizing: border-box;
}

.selector-header.open {
  border-color: var(--el-color-primary);
  box-shadow: 0 0 0 2px var(--el-color-primary-light-7);
}

.selector-header .placeholder {
  color: #909399;
  flex: 1;
}

.selector-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  border: 1px solid var(--el-border-color);
  border-top: none;
  border-radius: 0 0 4px 4px;
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 2000;
  max-height: 400px;
  overflow: hidden;
}

.selector-panel {
  display: flex;
  height: 350px;
  min-width: 400px;
}

.tree-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 400px;
}

.panel-header {
  padding: 12px;
  font-weight: 600;
  background-color: var(--el-fill-color-light);
  border-bottom: 1px solid var(--el-border-color);
  color: var(--el-text-color-primary);
}

.search-container {
  padding: 8px;
  border-bottom: 1px solid var(--el-border-color-light);
  background: var(--el-fill-color-lighter);
}

.search-container .el-input {
  width: 100%;
}

.tree-list {
  flex: 1;
  overflow: hidden;
}

.tree-node-content {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 4px 0;
}

.device-icon {
  color: #409EFF;
  font-size: 16px;
  flex-shrink: 0;
}

.company-icon {
  color: #67C23A;
  font-size: 16px;
  flex-shrink: 0;
}

.default-icon {
  color: #909399;
  font-size: 16px;
  flex-shrink: 0;
}

.node-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device-code {
  font-size: 12px;
  color: #909399;
  margin-left: 4px;
  flex-shrink: 0;
}

:deep(.tree-panel .el-tree) {
  background: white;
  padding: 8px;
}

:deep(.tree-panel .el-tree-node__content) {
  height: 36px;
  line-height: 36px;
  cursor: pointer;
}

:deep(.tree-panel .el-tree-node__expand-icon) {
  padding: 8px;
  cursor: pointer;
}

:deep(.tree-panel .el-tree-node:focus > .el-tree-node__content) {
  background-color: #f5f7fa;
}

:deep(.tree-panel .el-tree-node.is-current > .el-tree-node__content) {
  background-color: #ecf5ff;
  color: #409EFF;
  font-weight: 500;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  flex: 1;
  align-items: center;
  min-height: 24px;
}

.device-tag {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-icon {
  margin-left: auto;
  padding-left: 8px;
  display: flex;
  align-items: center;
  transition: transform 0.3s;
  flex-shrink: 0;
}

.dropdown-icon .el-icon {
  transition: all 0.3s;
  color: var(--el-text-color-secondary);
  font-size: 14px;
}

.dropdown-icon .reverse {
  transform: rotate(180deg);
}

.dropdown-icon:hover .el-icon {
  color: var(--el-color-primary);
}

.dropdown-icon:hover .el-icon[data-v-close] {
  color: var(--el-color-danger);
}

:deep(.el-scrollbar__bar.is-vertical) {
  width: 6px;
}

:deep(.el-scrollbar__thumb) {
  background-color: var(--el-color-info-light-5);
}

:deep(.el-scrollbar__thumb:hover) {
  background-color: var(--el-color-info-light-3);
}
</style>